import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useState } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { useDispatch } from "react-redux";
import AddButton, { DraftButton } from "../../../../components/buttons/button";
import CloseIcon from "../../../../assets/icons/close-icon";
import InputField from "../../../../components/forms/input-field";
import successToast from "../../../../components/helper-components/success-toast";
import errorToast from "../../../../components/helper-components/error-toast";
import { warehouseBinGenerateAction } from "../../../../redux/actions/inventory-action";

const DIMENSIONS = ["building", "floor", "zone", "rack", "row", "column"];

const rangeSchema = Yup.object().shape({
  start: Yup.number().min(0, "Min 0").required("Required"),
  end: Yup.number()
    .min(Yup.ref("start"), "End >= start")
    .required("Required"),
  pad: Yup.number().min(1, "Min 1").max(10, "Max 10").required("Required"),
});

const validationSchema = Yup.object().shape({
  bin_location_no: Yup.string().required("Bin location template is required"),
  building: rangeSchema,
  floor: rangeSchema,
  zone: rangeSchema,
  rack: rangeSchema,
  row: rangeSchema,
  column: rangeSchema,
});

const GenerateBinsModal = ({
  isModalOpen = false,
  setIsModalOpen,
  warehouseId,
  onGenerated = () => {},
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const initialValues = {
    bin_location_no: "",
    building: { start: 0, end: 0, pad: 1 },
    floor: { start: 0, end: 0, pad: 1 },
    zone: { start: 0, end: 0, pad: 1 },
    rack: { start: 0, end: 0, pad: 1 },
    row: { start: 0, end: 0, pad: 1 },
    column: { start: 0, end: 0, pad: 1 },
    max_quantity: 100,
    max_weight: "",
  };

  const onSubmit = (values) => {
    setLoading(true);
    const payload = {
      warehouse_id: warehouseId,
      bin_location_no: values.bin_location_no,
      max_quantity: values.max_quantity === "" ? null : Number(values.max_quantity),
      max_weight: values.max_weight === "" ? null : Number(values.max_weight),
    };
    DIMENSIONS.forEach((dim) => {
      payload[dim] = {
        start: Number(values[dim].start),
        end: Number(values[dim].end),
        pad: Number(values[dim].pad || 1),
      };
    });

    dispatch(
      warehouseBinGenerateAction(
        payload,
        (data) => {
          setLoading(false);
          successToast(
            `Generated ${data?.data?.length ?? "the"} bin(s) successfully`
          );
          setIsModalOpen(false);
          onGenerated();
        },
        (data) => {
          setLoading(false);
          errorToast(data?.message);
        }
      )
    );
  };

  return (
    <Transition show={isModalOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={() => setIsModalOpen(false)}
      >
        <div className="flex items-center justify-center min-h-screen px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black opacity-60" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4"
            enterTo="opacity-100 translate-y-0"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-4"
          >
            <div className="mx-auto w-full max-w-3xl my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded-xl">
              <Formik
                enableReinitialize={true}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
              >
                {({ handleSubmit }) => {
                  return (
                    <Form>
                      <div className="flex justify-between px-6 py-5">
                        <h2 className="fw_500">Generate Bins</h2>
                        <button
                          type="button"
                          className="close-button bg-transparent border-none p-0 hover:bg-787878"
                          onClick={() => setIsModalOpen(false)}
                        >
                          <CloseIcon />
                        </button>
                      </div>

                      <div className="px-6">
                        <InputField
                          type="text"
                          name="bin_location_no"
                          placeholder="e.g. B{building}-F{floor}-Z{zone}-R{rack}-{row}-{column}"
                          label="Bin Location No. Template"
                          isRequired={true}
                          labelWidth="12rem"
                          allowSpecial={true}
                        />
                        <p className="text-667085 fs_12 -mt-2 mb-4">
                          Use {"{building} {floor} {zone} {rack} {row} {column}"} as
                          placeholders — each is padded per its Pad value below.
                        </p>
                      </div>

                      <div className="px-6 max-h-[45vh] overflow-y-auto">
                        <div className="grid grid-cols-[7rem_1fr_1fr_1fr] gap-3 items-center mb-2">
                          <span className="fs_12 fw_600 text-667085">
                            Dimension
                          </span>
                          <span className="fs_12 fw_600 text-667085">Start</span>
                          <span className="fs_12 fw_600 text-667085">End</span>
                          <span className="fs_12 fw_600 text-667085">Pad</span>
                        </div>
                        {DIMENSIONS.map((dim) => (
                          <div
                            key={dim}
                            className="grid grid-cols-[7rem_1fr_1fr_1fr] gap-3 items-start"
                          >
                            <span className="fs_14 fw_500 capitalize pt-2">
                              {dim}
                            </span>
                            {["start", "end", "pad"].map((part) => (
                               <InputField
                                key={part}
                                type="number"
                                name={`${dim}.${part}`}
                                label=""
                                placeholder=""
                                isRequired={false}
                                containerClass="min-w-0"
                              />
                            ))}
                          </div>
                        ))}
                      </div>

                      <div className="flex gap-5 px-6 mt-3">
                        <div className="flex-1">
                          <InputField
                            type="number"
                            name="max_quantity"
                            placeholder="Optional"
                            label="Max Quantity"
                            isRequired={false}
                            labelWidth="8rem"
                          />
                        </div>
                        <div className="flex-1">
                          <InputField
                            type="number"
                            name="max_weight"
                            placeholder="Optional"
                            label="Max Weight"
                            isRequired={false}
                            labelWidth="8rem"
                          />
                        </div>
                      </div>

                      <div className="px-6 flex justify-between gap-4 py-5">
                        <DraftButton
                          onclkFn={() => setIsModalOpen(false)}
                          text="Cancel"
                        />
                        <AddButton
                          loading={loading}
                          onclkFn={handleSubmit}
                          type="submit"
                          className="bg-primary text-white px-8 py-2 rounded flex justify-center fw_500 text-sm"
                          text="Generate"
                          prefixIcon={false}
                        />
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default GenerateBinsModal;
